// frontend/src/utils/accountAccess.js
//
// "Has this account's access lapsed?" - one predicate, so the leads area and
// the subscription banner can never disagree about it on the same screen.
//
// NEVER a tier literal in a caller. `user_type` is the billing TIER (solo /
// professional / enterprise / recommender) and says nothing about whether the
// account is currently paying; the lifecycle STATUS and the subscription row
// do. And "is this tier billed by invoice" is not a question the browser
// answers either: the backend derives it (`entitlements.is_invoice_billed_tier`)
// and sends the answer as `user.invoice_billed` on `/users/me` and
// `/auth/session` - the `website_eligible` precedent (#349 R4-03). There is no
// tier set in this file any more.

/**
 * Is this account billed by invoice rather than by a self-serve subscription?
 *
 * READS THE SERVER'S DERIVED FLAG, `user.invoice_billed`, and nothing else.
 * Only an explicit `true` counts: a payload from an older backend (no flag), a
 * session still loading, or any other value is NOT invoice-billed, which keeps
 * every surface exactly as it was before #349 for those.
 *
 * Why it matters (#349, owner bug 2026-10-01): a Professional subscriber moved
 * to Enterprise keeps their OLD self-serve `subscriptions` row, closed
 * (`cancelled`) by design when the Enterprise billing account took over. That
 * row says nothing about the account any more. Every surface that derives
 * plan, expiry, renewal or "action needed" state from `user.subscription` must
 * therefore ask this first and, when it answers true, ignore the subscription:
 * the account's lifecycle STATUS is the fact, and the Enterprise billing
 * summary is the billing truth. An invoice-billed account is also never sent to
 * the self-serve checkout (`/payment`).
 *
 * A TRIALING account is never invoice-billed: the backend answers false for
 * it, and every trial surface keys on `status === "trial"` first anyway.
 *
 * @param {object|null|undefined} user the session user payload.
 * @returns {boolean}
 */
export const isInvoiceBilledAccount = (user) => Boolean(user) && user.invoice_billed === true;

/**
 * True when the account no longer receives new opportunities: the user row is
 * inactive, or the subscription behind it expired or was cancelled.
 *
 * THE ONLY COPY. It began as a verbatim lift of
 * `components/SubscriptionBanner.js`'s priority-3 branch, with a note saying
 * the banner would be folded onto it later; that fold has happened, so the
 * banner's "המנוי פג" and the leads area's lapsed card are now decided here,
 * once. Both are read on the same screen - a second copy of this rule is a
 * dashboard that can contradict itself, so do not inline it again.
 *
 * AN INVOICE-BILLED ACCOUNT IS DECIDED BY ITS STATUS ALONE. Its self-serve
 * subscription row, if any, is a leftover from before it moved to invoice
 * billing (`isInvoiceBilledAccount`), and reading that row's `cancelled` is
 * what told an active Enterprise customer "your subscription expired".
 * An INACTIVE invoice-billed account is still "lapsed" here - its service is
 * off (pilot not paid yet, pilot ended and paused, or cancelled) - but no
 * caller may answer that with the self-serve "renew" pitch: the banner and the
 * leads area's lapsed card render the account's own Enterprise notice instead
 * (`subscriptionView.resolveEnterpriseServiceNotice`), with no checkout CTA.
 *
 * Unknown / missing user is NOT lapsed: a not-yet-loaded session must not make
 * the dashboard tell a paying customer their subscription ended.
 *
 * @param {object|null|undefined} user the session user payload.
 * @returns {boolean}
 */
export const isAccountLapsed = (user) => {
  if (!user) return false;
  if (user.status === "inactive") return true;
  if (isInvoiceBilledAccount(user)) return false;
  return (
    user.subscription?.status === "expired" ||
    user.subscription?.status === "cancelled"
  );
};

export default isAccountLapsed;
